<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useChatStore } from '../stores/chat.js';
import ChatMessage from './ChatMessage.vue';
import ChatInput from './ChatInput.vue';
import {
  MessageSquareText,
  Search,
  Users,
  Network,
  BarChart3,
  Database,
  HelpCircle,
} from 'lucide-vue-next';

const chatStore = useChatStore();
const messagesContainer = ref(null);
const chatInputRef = ref(null);

const messages = computed(() => chatStore.currentMessages);
const isLoading = computed(() => {
  if (!chatStore.isSending) return false;
  // Streaming already has a placeholder assistant message in the array — don't duplicate
  const last = messages.value[messages.value.length - 1];
  return !last || last.role !== 'assistant';
});
const hasMessages = computed(() => messages.value.length > 0);
const error = computed(() => chatStore.error);

watch(
  () => {
    const msgs = messages.value;
    const last = msgs[msgs.length - 1];
    return msgs.length + (last?.content?.length || 0);
  },
  () => {
    nextTick(() => {
      scrollToBottom();
    });
  }
);

function scrollToBottom() {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
}

async function handleSend(content) {
  try {
    await chatStore.sendMessage(content);
  } catch (err) {
    console.error('Failed to send message:', err);
  }
}

function handleStop() {
  chatStore.stopGenerating();
}

const examplePrompts = [
  {
    icon: Search,
    title: 'Partner lookup',
    prompt: 'What does Moveworks do for Ciena?',
  },
  {
    icon: Users,
    title: 'Business units',
    prompt: 'Which partners support Blue Planet?',
  },
  {
    icon: Network,
    title: 'VPN connections',
    prompt: 'Which partners connect via VPN?',
  },
  {
    icon: BarChart3,
    title: 'Employee count',
    prompt: 'Partners with over 50 employees?',
  },
  {
    icon: Database,
    title: 'Database stats',
    prompt: 'How many companies and categories exist?',
  },
  {
    icon: HelpCircle,
    title: 'Data overview',
    prompt: 'What data do you have on partners?',
  },
];

function handleExampleClick(prompt) {
  handleSend(prompt);
}

onMounted(() => {
  chatInputRef.value?.focusInput();
});
</script>

<template>
  <div class="flex flex-col h-full bg-main-bg">
    <!-- Messages area -->
    <div ref="messagesContainer" class="flex-1 overflow-y-auto px-6">
      <!-- Welcome screen when no messages -->
      <div v-if="!hasMessages" class="h-full flex flex-col justify-center">
        <div class="max-w-3xl mx-auto px-6">
          <div class="flex flex-col items-center text-center">
            <!-- Logo -->
            <div class="w-16 h-16 mb-6 rounded-full bg-primary flex items-center justify-center">
              <MessageSquareText :size="32" class="text-white" />
            </div>

            <!-- Title -->
            <h1 class="text-3xl font-semibold text-text-primary mb-4">Ciena AI Assistant</h1>

            <!-- Subtitle -->
            <p class="text-text-secondary text-base mb-10 max-w-md leading-relaxed">
              Ask questions about companies, search the knowledge base, or explore available data.
            </p>

            <!-- Example prompts -->
            <div class="w-full grid gap-3 grid-cols-2 sm:grid-cols-3">
              <button
                v-for="example in examplePrompts"
                :key="example.title"
                @click="handleExampleClick(example.prompt)"
                class="flex flex-col items-start p-4 rounded-xl border border-input-border bg-transparent hover:bg-sidebar-hover/50 transition-all duration-200 text-left"
              >
                <component :is="example.icon" :size="20" class="text-primary mb-3" />
                <span class="text-sm font-medium text-text-primary mb-1.5">
                  {{ example.title }}
                </span>
                <span class="text-xs text-text-muted leading-relaxed">
                  {{ example.prompt }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Messages list -->
      <div v-else class="py-8">
        <div class="max-w-3xl mx-auto px-6">
          <ChatMessage
            v-for="(message, index) in messages"
            :key="message.id"
            :message="message"
            :isTyping="
              chatStore.isSending &&
              index === messages.length - 1 &&
              message.role === 'assistant' &&
              !message.content
            "
          />

          <ChatMessage
            v-if="isLoading"
            :message="{ role: 'assistant', content: '' }"
            :isTyping="true"
          />
        </div>
      </div>
    </div>

    <!-- Error display -->
    <div v-if="error && !isLoading" class="shrink-0 bg-red-500/10 border-t border-red-500/20 mx-6">
      <div class="max-w-3xl mx-auto px-6 py-2 flex items-center justify-between">
        <span class="text-sm text-red-400">{{ error }}</span>
        <button
          @click="chatStore.clearError()"
          class="text-xs text-red-400 hover:text-red-300 underline"
        >
          Dismiss
        </button>
      </div>
    </div>

    <!-- Input area -->
    <ChatInput
      ref="chatInputRef"
      :loading="chatStore.isSending"
      @send="handleSend"
      @stop="handleStop"
    />
  </div>
</template>
<style lang="scss" scoped>
@tailwind base;
@tailwind components;
@tailwind utilities;
@import './../main.css';
</style>
